import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  ChevronDown, ChevronRight, Search, Building2, Send, CheckCircle2,
  ArrowLeft, ArrowRight, CalendarRange, Inbox, X, AlertTriangle,
  Landmark, Eye, EyeOff, User,
} from "lucide-react";
import vayLogo from "../assets/vay-logo.png"

// ---------------------------------------------------------------------------
// Design tokens (shared) — themed to evoke the Vayana product screenshots:
// maroon/red gradient header, blue primary actions, white card lists.
// ---------------------------------------------------------------------------
const TOKENS = `
  --navy: #1F2F73;
  --maroon: #C1272D;
  --maroon-dark: #12194A;
  --blue: #2A3F8C;
  --blue-dark: #17399E;
  --bg: #F5F6F8;
  --card: #FFFFFF;
  --border: #E3E5EA;
  --ink: #1F2430;
  --muted: #667085;
  --muted2: #98A2B3;
  --green-bg: #E7F5EA;
  --green-ink: #1E7B34;
  --amber-bg: #FFF4E0;
  --amber-ink: #8A5B00;
  --teal-bg: #E4F0F4;
  --teal-ink: #0F5C74;
  --red-bg: #FDEEDD;
  --red-ink: #B5651D;
  --grey-bg: #EEF1F4;
  --grey-ink: #51606B;
`;

const money = (n) => "\u20B9" + Math.round(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const fmtDate = (iso) =>
  new Date(iso + "T00:00:00").toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
const isoToDMY = (iso) => {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};
const dmyToIso = (str) => {
  const m = str.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return null;
  const [, d, mo, y] = m;
  return `${y}-${mo}-${d}`;
};
const FORBIDDEN = "\u{1F6AB}";

// Fixed "today" for this prototype, matching the 18th-of-month deadline used
// throughout the workflow.
const TODAY_ISO = "2026-08-14";
const TODAY_LABEL = "14 Aug 2026";
// The Report to GSTN submission date, distinct from "today" shown elsewhere.
const REPORT_DATE_ISO = "2026-08-14";
const REPORT_DATE_LABEL = "14 Aug 2026";

const STATUS_META = {
  pending: { label: "Payment Pending", cls: "pending" },
  inflight: { label: "Payment Initiated", cls: "inflight" },
  paid: { label: "Payment Done", cls: "paid" },
  failed: { label: "Payment Failed", cls: "failed" },
};

function StatusBadge({ status, reason, short }) {
  const m = STATUS_META[status];
  const label = short ? m.label.replace(/^Payment\s+/, "") : m.label;
  const hasTip = status === "failed" && reason;
  return (
    <span className={"gv-badge " + m.cls + (hasTip ? " tip" : "")} title={hasTip ? reason : undefined}>
      {label}
    </span>
  );
}

// Vayana-style "V" mark — multiple parallel red and navy diagonal strokes
// converging into a V, echoing the uploaded logo file.
function VMark({ size = 26 }) {
  // Three parallel strokes per side (each side sharing one slant, just
  // offset), not converging to a single point — red on the left, navy on
  // the right, overlapping each other near the bottom.
  const red = ["#B31E24", "#C1272D", "#DE6B5E"];
  const navy = ["#1A2560", "#1F2F73", "#2B3F8C"];
  const width = Math.round((size * 62) / 52); // preserve the viewBox's aspect ratio
  return (
    <svg width={width} height={size} viewBox="0 0 62 52" fill="none" aria-hidden="true">
      {red.map((c, i) => (
        <line
          key={"r" + i}
          x1={2 + i * 7} y1="4"
          x2={28 + i * 7} y2="48"
          stroke={c} strokeWidth="4.5" strokeLinecap="round"
        />
      ))}
      {navy.map((c, i) => (
        <line
          key={"n" + i}
          x1={60 - i * 7} y1="4"
          x2={34 - i * 7} y2="48"
          stroke={c} strokeWidth="4.5" strokeLinecap="round"
        />
      ))}
    </svg>
  );
}

function VLogoFull({ markSize = 24 }) {
  return (
    <span className="gv-logofull">
      <img src={vayLogo} alt={VMark} height={markSize} />
      <span className="gv-logotext">VAYANA<sup>TM</sup></span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Mock master data
// ---------------------------------------------------------------------------
// Role model: the logged-in company is the BUYER — an SME — and it may hold
// several GSTINs (one per state registration). Each invoice belongs to one
// of the buyer's GSTINs and is raised by a SELLER — also an SME — who may
// likewise hold more than one GSTIN. GST is owed by the buyer to the seller,
// tracked per (buyer GSTIN) x (seller GSTIN) pair.

const BUYER = { name: "Vishwas Enterprises Pvt Ltd" };
const BUYER_BANK = { bank: "HDFC Bank", account: "50100234567890", ifsc: "HDFC0000123" };
const BUYER_GSTINS = [
  { gstin: "27AAFCM4321L1Z9", state: "Maharashtra" },
  { gstin: "24AAFCM4321L1Z3", state: "Gujarat" },
  { gstin: "19AAFCM4321L1Z8", state: "West Bengal" },
];

const SELLERS = [
  { id: "s1", name: "Shree Balaji Textiles Pvt Ltd", gstins: ["27AAACR4849R1Z0"], bankName: "HDFC Bank", bankAccount: "0123456789012", ifsc: "HDFC0001234" },
  { id: "s2", name: "Om Precision Engineering Pvt Ltd", gstins: ["27AAACT2727Q1ZW"], bankName: "ICICI Bank", bankAccount: "0234567890123", ifsc: "ICIC0002345" },
  { id: "s3", name: "Krishna Packaging Industries", gstins: ["29AAACI4741P1ZW"], bankName: "State Bank of India", bankAccount: "0345678901234", ifsc: "SBIN0003456" },
  { id: "s4", name: "Sunrise Electricals & Hardware", gstins: ["27AAACL0140P1ZV"], bankName: "Axis Bank", bankAccount: "0456789012345", ifsc: "AXIS0004567" },
  { id: "s5", name: "Ganesh Auto Components", gstins: ["07AAACB2894G1Z1"], bankName: "Kotak Mahindra Bank", bankAccount: "0567890123456", ifsc: "KKBK0005678" },
];

// Flattened list of one entry per (seller, GSTIN) — every seller here has a
// single GSTIN.
const GSTIN_UNITS = SELLERS.flatMap((s) =>
  s.gstins.map((g) => ({ key: `${s.id}|${g}`, sellerId: s.id, sellerName: s.name, gstin: g, bankName: s.bankName, bankAccount: s.bankAccount, ifsc: s.ifsc }))
);

const RATE = 0.18;

/*const FAILURE_REASONS = [
  "Beneficiary account mismatch",
  "Invalid IFSC code",
  "Insufficient mandate limit",
  "Bank server timeout",
  "Beneficiary account frozen",
];*/

function mockIrn(seed) {
  // A real e-invoice IRN is a 64-character hex string. Build one
  // deterministically from the invoice number so it's stable across renders.
  let out = "";
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  while (out.length < 64) {
    h = (h * 1103515245 + 12345) >>> 0;
    out += h.toString(16).padStart(8, "0");
  }
  return out.slice(0, 64);
}
function hashOf(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return h;
}
function mockUtr(seed) {
  const h = hashOf(seed).toString(16).toUpperCase().padStart(10, "0");
  return "UTR" + h.slice(0, 10);
}
/*function failureReasonFor(seed) {
  return FAILURE_REASONS[hashOf(seed) % FAILURE_REASONS.length];
}*/
// An invoice is eligible to go out in the next payout file if it's pending
// (freshly ready) or previously failed (auto-selected for another attempt).
function isSendable(inv) {
  return (inv.status === "pending" || inv.status === "failed") && inv.selected;
}

function mk(sellerId, gstin, buyerGstin, no, date, value, status, paidDate) {
  return {
    id: no,
    sellerId,
    gstin,
    buyerGstin,
    invoiceNo: no,
    irn: mockIrn(no),
    date,
    value,
    gst: Math.round(value * RATE),
    status, // 'pending' | 'paid' (seed only starts in one of these two)
    paidDate: paidDate || null, // when actually paid, if different from the report run date
    selected: true,
    inConsolidation: false,
    failureReason: null,
  };
}

const BG1 = "27AAFCM4321L1Z9"; // Maharashtra
const BG2 = "24AAFCM4321L1Z3"; // Gujarat
// BG3 ("19AAFCM4321L1Z8") intentionally has no invoices, to demonstrate
// that the My GSTIN listing only shows buyer GSTINs with activity.

const SEED_INVOICES = [
  // Shree Balaji Textiles
  mk("s1", "27AAACR4849R1Z0", BG1, "INV-3280", "2026-07-01", 220000, "paid"),
  mk("s1", "27AAACR4849R1Z0", BG1, "INV-3299", "2026-07-07", 175000, "paid"),
  mk("s1", "27AAACR4849R1Z0", BG1, "INV-3312", "2026-07-12", 485000, "pending"),
  mk("s1", "27AAACR4849R1Z0", BG1, "INV-3340", "2026-07-15", 210500, "pending"),
  mk("s1", "27AAACR4849R1Z0", BG1, "INV-3365", "2026-07-18", 642000, "pending"),
  mk("s1", "27AAACR4849R1Z0", BG1, "INV-3390", "2026-07-22", 158750, "pending"),
  mk("s1", "27AAACR4849R1Z0", BG1, "INV-3410", "2026-07-26", 325000, "pending"),
  mk("s1", "27AAACR4849R1Z0", BG1, "INV-3428", "2026-07-29", 95400, "pending"),
  // Om Precision Engineering
  mk("s2", "27AAACT2727Q1ZW", BG2, "INV-8770", "2026-07-02", 300000, "paid"),
  mk("s2", "27AAACT2727Q1ZW", BG2, "INV-8790", "2026-07-09", 410000, "paid"),
  mk("s2", "27AAACT2727Q1ZW", BG2, "INV-8801", "2026-07-05", 920000, "pending"),
  mk("s2", "27AAACT2727Q1ZW", BG2, "INV-8830", "2026-07-14", 410250, "pending"),
  mk("s2", "27AAACT2727Q1ZW", BG2, "INV-8862", "2026-07-21", 275000, "pending"),
  mk("s2", "27AAACT2727Q1ZW", BG2, "INV-8890", "2026-07-30", 560800, "pending"),
  // Krishna Packaging Industries
  mk("s3", "29AAACI4741P1ZW", BG1, "INV-5520", "2026-07-01", 180000, "paid"),
  mk("s3", "29AAACI4741P1ZW", BG2, "INV-5530", "2026-07-05", 95000, "paid"),
  mk("s3", "29AAACI4741P1ZW", BG1, "INV-5541", "2026-07-03", 300000, "pending"),
  mk("s3", "29AAACI4741P1ZW", BG2, "INV-5560", "2026-07-09", 145600, "pending"),
  mk("s3", "29AAACI4741P1ZW", BG1, "INV-5588", "2026-07-16", 288900, "pending"),
  mk("s3", "29AAACI4741P1ZW", BG2, "INV-5610", "2026-07-24", 112300, "pending"),
  mk("s3", "29AAACI4741P1ZW", BG1, "INV-5635", "2026-07-31", 450000, "pending"),
  // Sunrise Electricals & Hardware
  mk("s4", "27AAACL0140P1ZV", BG2, "INV-2180", "2026-07-03", 260000, "paid"),
  mk("s4", "27AAACL0140P1ZV", BG1, "INV-2195", "2026-07-12", 190000, "paid"),
  mk("s4", "27AAACL0140P1ZV", BG2, "INV-2201", "2026-07-08", 780000, "pending"),
  mk("s4", "27AAACL0140P1ZV", BG1, "INV-2230", "2026-07-19", 360000, "pending"),
  mk("s4", "27AAACL0140P1ZV", BG2, "INV-2255", "2026-07-27", 215000, "pending"),
  // Ganesh Auto Components
  mk("s5", "07AAACB2894G1Z1", BG1, "INV-8990", "2026-07-01", 150000, "paid"),
  mk("s5", "07AAACB2894G1Z1", BG2, "INV-9010", "2026-07-06", 220000, "paid"),
  mk("s5", "07AAACB2894G1Z1", BG1, "INV-9001", "2026-07-02", 190000, "pending"),
  mk("s5", "07AAACB2894G1Z1", BG2, "INV-9025", "2026-07-11", 260500, "pending"),
  mk("s5", "07AAACB2894G1Z1", BG1, "INV-9050", "2026-07-20", 335000, "pending"),
  mk("s5", "07AAACB2894G1Z1", BG2, "INV-9078", "2026-07-28", 105750, "pending"),
  // Additional invoices already paid ahead of the 14 Aug reporting run, so
  // the GSTN report shows a genuine mix of payment dates.
  mk("s1", "27AAACR4849R1Z0", BG1, "INV-3450", "2026-07-30", 130000, "paid", "2026-08-08"),
  mk("s3", "29AAACI4741P1ZW", BG2, "INV-5650", "2026-07-29", 88000, "paid", "2026-08-05"),
  mk("s5", "07AAACB2894G1Z1", BG1, "INV-9090", "2026-07-25", 112000, "paid", "2026-08-10"),
];

const DEFAULT_FROM = "2026-07-01";
const DEFAULT_TO = "2026-07-31";

function freshInvoices() {
  return SEED_INVOICES.map((i) => ({ ...i }));
}

// ---------------------------------------------------------------------------
// Shared indeterminate-aware checkbox
// ---------------------------------------------------------------------------

function Check({ checked, indeterminate, onChange, label }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = !!indeterminate && !checked;
  }, [indeterminate, checked]);
  return (
    <input
      ref={ref}
      type="checkbox"
      className="gv-chk"
      checked={checked}
      onChange={onChange}
      aria-label={label}
      onClick={(e) => e.stopPropagation()}
    />
  );
}

// ---------------------------------------------------------------------------
// Shared modal shell
// ---------------------------------------------------------------------------

function Modal({ title, subtitle, onClose, children, footer, wide, xwide }) {
  return (
    <div className="gv-modal-overlay" onClick={onClose}>
      <div className={"gv-modal" + (wide ? " wide" : "") + (xwide ? " xwide" : "")} onClick={(e) => e.stopPropagation()}>
        <div className="gv-modal-head">
          <div>
            <h2>{title}</h2>
            {subtitle && <p>{subtitle}</p>}
          </div>
          <button className="gv-modal-close" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </div>
        <div className="gv-modal-body">{children}</div>
        {footer && <div className="gv-modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

// Grouped-by-UTR view: one payment instruction (UTR) per card, with the
// common bank/buyer/seller details shown once and every IRN it covers
// listed underneath.
function UtrGroupTable({ groups, detailed }) {
  if (groups.length === 0) return <div className="gv-empty">No successful payments to notify this round.</div>;
  const total = groups.reduce((a, g) => a + g.amount, 0);
  const irnTotal = groups.reduce((a, g) => a + g.items.length, 0);
  return (
    <div className="gv-irnlist">
      {groups.map((g) => (
        <div className="gv-irncard" key={g.key}>
          <div className="gv-irncard-row gv-irncard-row1">
            <span className="l">UTR</span>
            <span className="v gv-utr">{g.utr}</span>
          </div>
          <div className="gv-irncard-row gv-irncard-row2">
            <div className="gv-irnfield">
              <span className="l">Payment Date</span>
              <span className="v">{fmtDate(g.paymentDate)}</span>
            </div>
            <div className="gv-irnfield">
              <span className="l">Buyer</span>
              <span className="v">{g.buyerName} ({g.buyerGstin})</span>
            </div>
            <div className="gv-irnfield">
              <span className="l">Seller</span>
              <span className="v">{g.sellerName} ({g.sellerGstin})</span>
            </div>
            <div className="gv-irnfield">
              <span className="l">GST Amount</span>
              <span className="v">{money(g.amount)}</span>
            </div>
            <div className="gv-irnfield">
              <span className="l">From Bank</span>
              <span className="v">{g.fromBank} | {g.fromAccount} | {g.fromIfsc}</span>
            </div>
            <div className="gv-irnfield">
              <span className="l">To Bank</span>
              <span className="v">{g.toBank} | {g.toAccount} | {g.toIfsc}</span>
            </div>
          </div>
          <div className="gv-irncard-row gv-irncard-row3">
            <span className="l">Invoices Covered ({g.items.length})</span>
            {detailed && (
              <div className="gv-invoiceline gv-invoiceline-head">
                <span className="gv-il-irn">IRN</span>
                <span className="gv-il-date">Invoice Date</span>
                <span className="gv-il-net">Invoice Value</span>
                <span className="gv-il-gst">GST Paid</span>
              </div>
            )}
            <div className="gv-invoicelines">
              {g.items.map((item) => (
                <div className={"gv-invoiceline" + (detailed ? " gv-invoiceline-detailed" : "")} key={item.irn}>
                  <span className="gv-irn gv-il-irn" title={item.irn}>{item.irn}</span>
                  {detailed && <span className="gv-il-date">{fmtDate(item.date)}</span>}
                  {detailed && <span className="gv-il-net">{money(item.netAmount)}</span>}
                  <span className="gv-invoiceline-amt gv-il-gst">{money(item.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
      <div className="gv-irnlist-total">
        <span>Total - {groups.length} UTR{groups.length !== 1 ? "s" : ""} - {irnTotal} invoice{irnTotal !== 1 ? "s" : ""}</span>
        <span className="gv-irnlist-total-amt">{money(total)}</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// LOGIN — password + OTP (demo credentials: password 123456, OTP 000000)
// ---------------------------------------------------------------------------

function LoginScreen({ onLoginSuccess }) {
  const [loginId, setLoginId] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  function handleSubmit(e) {
    e.preventDefault();
    onLoginSuccess();
  }

  return (
    <div className="gv-login-root">
      <style>{`.gv-login-root{${TOKENS}}` + SHARED_CSS + LOGIN_CSS}</style>
      <div className="gv-login-card">
        <div className="gv-login-logo"><VLogoFull markSize={30} /></div>

        <form onSubmit={handleSubmit} className="gv-login-form">
          <h1>Sign In</h1>
          <p className="gv-login-sub">Enter your credentials to continue to Payable Invoices.</p>

          <label htmlFor="gv-login-id">Email or Mobile</label>
          <input
            id="gv-login-id"
            type="text"
            value={loginId}
            onChange={(e) => setLoginId(e.target.value)}
            placeholder="you@company.com"
          />

          <label htmlFor="gv-login-pwd">Password</label>
          <div className="gv-login-pwdwrap">
            <input
              id="gv-login-pwd"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
            />
            <button type="button" onClick={() => setShowPassword((s) => !s)} aria-label={showPassword ? "Hide password" : "Show password"}>
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>

          <button type="submit" className="gv-genbtn gv-login-submit" onClick={handleSubmit}>Sign In</button>
        </form>
      </div>
    </div>
  );
}

// Shared header bar used by both screens.
// Small account dropdown — Profile (no-op) and Logout.
function UserMenu({ onLogout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  return (
    <div className="gv-usermenu" ref={ref}>
      <button className="gv-usericon" onClick={() => setOpen((o) => !o)} aria-label="Account menu" title="Account">
        <User size={16} />
      </button>
      {open && (
        <div className="gv-usermenu-dropdown">
          <button className="gv-usermenu-item" onClick={() => setOpen(false)}>Profile</button>
          <button className="gv-usermenu-item" onClick={() => { setOpen(false); onLogout(); }}>Logout</button>
        </div>
      )}
    </div>
  );
}

function TopBar({ onLogoClick, onLogout, children }) {
  return (
    <div className="gv-topbar">
      <div className="gv-topbar-row1">
        <div className="gv-topbar-left">
          <button className="gv-logo" onClick={onLogoClick} title="Home">
            <VLogoFull />
          </button>
          <p className="gv-eyebrow">Payable Invoices</p>
        </div>
        <div className="gv-topbar-right">
          <div className="gv-topbar-chips">
            <span className="gv-rolechip"><Landmark size={12} /> Buyer: {BUYER.name}</span>
            <span className="gv-todaydate">{TODAY_LABEL}</span>
          </div>
          <UserMenu onLogout={onLogout} />
        </div>
      </div>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SCREEN 1 — My GSTIN listing
// ---------------------------------------------------------------------------

function InvoiceListScreen({ invoices, onConsolidate, onLogoClick, onLogout, gstnReportedGstins, setGstnReportedGstins }) {
  const [from, setFrom] = useState(DEFAULT_FROM);
  const [to, setTo] = useState(DEFAULT_TO);
  const [fromText, setFromText] = useState(isoToDMY(DEFAULT_FROM));
  const [toText, setToText] = useState(isoToDMY(DEFAULT_TO));
  const [selectedMyGstin, setSelectedMyGstin] = useState(null);
  const [expandedGstins, setExpandedGstins] = useState(() => new Set());
  const [warningOpen, setWarningOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [toast, setToast] = useState(null);

  function flashToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  function handleFromChange(v) {
    setFromText(v);
    const iso = dmyToIso(v);
    if (iso) setFrom(iso);
  }
  function handleToChange(v) {
    setToText(v);
    const iso = dmyToIso(v);
    if (iso) setTo(iso);
  }
  function toggleExpand(gstin) {
    setExpandedGstins((prev) => {
      const next = new Set(prev);
      next.has(gstin) ? next.delete(gstin) : next.add(gstin);
      return next;
    });
  }

  const sellerName = (id) => SELLERS.find((s) => s.id === id)?.name || id;

  const inPeriod = useMemo(() => invoices.filter((i) => i.date >= from && i.date <= to), [invoices, from, to]);

  // Overall totals across all of the buyer's GSTINs.
  const totals = useMemo(() => {
    const t = { count: inPeriod.length, value: 0, gst: 0, pendingGst: 0, inflightGst: 0, failedGst: 0, paidGst: 0 };
    inPeriod.forEach((i) => {
      t.value += i.value;
      t.gst += i.gst;
      if (i.status === "pending") t.pendingGst += i.gst;
      else if (i.status === "inflight") t.inflightGst += i.gst;
      else if (i.status === "failed") t.failedGst += i.gst;
      else if (i.status === "paid") t.paidGst += i.gst;
    });
    return t;
  }, [inPeriod]);

  const counts = useMemo(() => {
    const c = { pending: 0, inflight: 0, paid: 0, failed: 0 };
    inPeriod.forEach((i) => (c[i.status] += 1));
    return c;
  }, [inPeriod]);

  // One row per buyer GSTIN that actually has invoices in the period.
  const myGstinRows = useMemo(() => {
    return BUYER_GSTINS.map((bg) => {
      const invs = inPeriod.filter((i) => i.buyerGstin === bg.gstin);
      if (invs.length === 0) return null;
      const row = {
        gstin: bg.gstin, state: bg.state,
        count: invs.length, value: 0, gst: 0,
        pendingGst: 0, pendingCount: 0,
        inflightGst: 0, inflightCount: 0,
        failedGst: 0, failedCount: 0,
        paidGst: 0, paidCount: 0,
      };
      invs.forEach((i) => {
        row.value += i.value;
        row.gst += i.gst;
        if (i.status === "pending") { row.pendingGst += i.gst; row.pendingCount += 1; }
        else if (i.status === "inflight") { row.inflightGst += i.gst; row.inflightCount += 1; }
        else if (i.status === "failed") { row.failedGst += i.gst; row.failedCount += 1; }
        else if (i.status === "paid") { row.paidGst += i.gst; row.paidCount += 1; }
      });
      row.hasInConsolidation = invs.some((i) => i.inConsolidation);
      row.invoices = [...invs].sort((a, b) => (a.status === "paid") - (b.status === "paid"));
      return row;
    }).filter(Boolean).sort((a, b) => a.gstin.localeCompare(b.gstin));
  }, [inPeriod]);

  const effectiveSelected = selectedMyGstin || (myGstinRows[0] ? myGstinRows[0].gstin : null);
  const selectedRow = myGstinRows.find((r) => r.gstin === effectiveSelected) || null;

  // UTR-grouped report of payments done for the selected GSTIN, between each
  // invoice's own date and 14 Aug, for invoices dated up to the end of July.
  const reportGroups = useMemo(() => {
    const byUnit = {};
    invoices
      .filter((i) => i.status === "paid" && i.date <= DEFAULT_TO && i.date <= REPORT_DATE_ISO)
      .forEach((i) => {
        const paymentDate = i.paidDate || REPORT_DATE_ISO;
        const key = i.sellerId + "|" + i.gstin + "|" + i.buyerGstin + "|" + paymentDate;
        if (!byUnit[key]) {
          const seller = SELLERS.find((s) => s.id === i.sellerId);
          byUnit[key] = {
            key,
            utr: mockUtr(key),
            paymentDate,
            fromBank: BUYER_BANK.bank, fromAccount: BUYER_BANK.account, fromIfsc: BUYER_BANK.ifsc,
            toBank: seller.bankName, toAccount: seller.bankAccount, toIfsc: seller.ifsc,
            buyerName: BUYER.name, buyerGstin: i.buyerGstin,
            sellerName: seller.name, sellerGstin: i.gstin,
            items: [],
            amount: 0,
          };
        }
        byUnit[key].items.push({ irn: i.irn, amount: i.gst });
        byUnit[key].amount += i.gst;
      });
    return Object.values(byUnit).sort((a, b) => a.paymentDate.localeCompare(b.paymentDate));
  }, [invoices]);

  function confirmReport() {
    setWarningOpen(false);
    setGstnReportedGstins((prev) => new Set(prev).add(effectiveSelected));
    setReportOpen(true);
  }

  function handleReportClick() {
    if (gstnReportedGstins.has(effectiveSelected)) {
      flashToast("Payments are already reported to GSTN for the invoices generated between 1st to 31st of July 2026.");
      return;
    }
    setWarningOpen(true);
  }

  return (
    <div className="gv-root">
      <style>{`.gv-root{${TOKENS}}` + SHARED_CSS + LIST_CSS}</style>

      <TopBar onLogoClick={onLogoClick} onLogout={onLogout}>
        <div className="gv-title-row">
          <div>
            <h1 className="gv-h1">GST Pending Payment</h1>
            <p className="gv-sub">
              Pick one GSTIN to proceed to pay GST to sellers for the selected period.
              Pay by 14th Aug 2026.
            </p>
          </div>
          <div className="gv-headright">
            <div className="gv-periodbox">
              <CalendarRange size={14} />
              <input
                type="text"
                inputMode="numeric"
                placeholder="dd/mm/yyyy"
                maxLength={10}
                value={fromText}
                onChange={(e) => handleFromChange(e.target.value)}
                className="gv-dateinput"
              />
              <span className="gv-periodto">to</span>
              <input
                type="text"
                inputMode="numeric"
                placeholder="dd/mm/yyyy"
                maxLength={10}
                value={toText}
                onChange={(e) => handleToChange(e.target.value)}
                className="gv-dateinput"
              />
              {from === DEFAULT_FROM && to === DEFAULT_TO && <span className="gv-periodpill">Last Month</span>}
            </div>
          </div>
        </div>
      </TopBar>

      <div className="gv-main">
        <div className="gv-summary gv-summary-flex">
          <div className="gv-card">
            <p className="gv-card-label">Invoice Value <span className="gv-card-label-note">(Net + GST)</span></p>
            <div className="gv-card-value">{money(totals.value + totals.gst)}</div>
            <p className="gv-card-sub">{totals.count} invoices</p>
          </div>
          <div className="gv-card gv-card-alert">
            <p className="gv-card-label">Pending Payment</p>
            <div className="gv-card-value accent-amber">{money(totals.pendingGst)}</div>
            <p className="gv-card-sub">{counts.pending} invoices available</p>
          </div>
          <div className="gv-card gv-card-progress">
            <p className="gv-card-label">Payment Initiated</p>
            <div className="gv-card-value accent-teal">{money(totals.inflightGst)}</div>
            <p className="gv-card-sub">{counts.inflight} awaiting bank</p>
          </div>
          <div className="gv-card gv-card-danger">
            <p className="gv-card-label">Failed Transactions</p>
            <div className="gv-card-value accent-red">{money(totals.failedGst)}</div>
            <p className="gv-card-sub">{counts.failed} need attention</p>
          </div>
          <div className="gv-card gv-card-success">
            <p className="gv-card-label">Paid</p>
            <div className="gv-card-value accent-green">{money(totals.paidGst)}</div>
            <p className="gv-card-sub">{counts.paid} invoices settled</p>
          </div>
        </div>

        <div className="gv-table-wrap gv-table-cards">
          <div className="gv-gstin-thead">
            <div></div>
            <div></div>
            <div>My GSTIN</div>
            <div>Invoice Value</div>
            <div>Pending Payment</div>
            <div>Payment Initiated</div>
            <div>Failed</div>
            <div>Paid</div>
          </div>
          {myGstinRows.length === 0 && <div className="gv-empty">No invoices in this period.</div>}
          {myGstinRows.map((r) => {
            const isOpen = expandedGstins.has(r.gstin);
            return (
              <React.Fragment key={r.gstin}>
                <div
                  className={"gv-gstin-row" + (effectiveSelected === r.gstin ? " selected" : "")}
                  onClick={() => setSelectedMyGstin(r.gstin)}
                >
                  <button
                    type="button"
                    className="gv-gstin-chevron"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleExpand(r.gstin); }}
                    aria-label="Show invoices"
                  >
                    {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </button>
                  <span className="gv-gstin-radio">
                    <input
                      type="radio"
                      name="mygstin"
                      checked={effectiveSelected === r.gstin}
                      onChange={() => setSelectedMyGstin(r.gstin)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </span>
                  <span className="gv-gstin-cell">
                    <span className="gv-gstin-value">{r.gstin}</span>
                    <span className="gv-gstin-sub">{r.state}</span>
                  </span>
                  <span className="gv-gstin-metric">
                    <span className="gv-gstin-amt">{money(r.value + r.gst)}</span>
                    <span className="gv-gstin-count">{r.count} invoices</span>
                  </span>
                  <span className="gv-gstin-metric">
                    <span className="gv-gstin-amt accent-amber">{money(r.pendingGst)}</span>
                    <span className="gv-gstin-count">{r.pendingCount} invoices</span>
                  </span>
                  <span className="gv-gstin-metric">
                    <span className="gv-gstin-amt accent-teal">{money(r.inflightGst)}</span>
                    <span className="gv-gstin-count">{r.inflightCount} invoices</span>
                  </span>
                  <span className="gv-gstin-metric">
                    <span className="gv-gstin-amt accent-red">{money(r.failedGst)}</span>
                    <span className="gv-gstin-count">{r.failedCount} invoices</span>
                  </span>
                  <span className="gv-gstin-metric">
                    <span className="gv-gstin-amt accent-green">{money(r.paidGst)}</span>
                    <span className="gv-gstin-count">{r.paidCount} invoices</span>
                  </span>
                </div>
                {isOpen && (
                  <div className="gv-gstin-drilldown">
                    {r.invoices.map((i) => (
                      <div className="gv-invcard" key={i.id}>
                        <div className="gv-invcard-row1">
                          <span className="l">IRN</span>
                          <span className="v gv-irn" title={i.irn}>{i.irn}</span>
                        </div>
                        <div className="gv-invcard-row2">
                          <div className="gv-irnfield">
                            <span className="l">Seller</span>
                            <span className="v">{sellerName(i.sellerId)}</span>
                          </div>
                          <div className="gv-irnfield">
                            <span className="l">Date</span>
                            <span className="v">{fmtDate(i.date)}</span>
                          </div>
                          <div className="gv-irnfield">
                            <span className="l">Invoice Value</span>
                            <span className="v">{money(i.value + i.gst)}</span>
                          </div>
                          <div className="gv-irnfield">
                            <span className="l">GST Amount</span>
                            <span className="v">{money(i.gst)}</span>
                          </div>
                          <div className="gv-irnfield">
                            <span className="l">Status</span>
                            <span className="v"><StatusBadge status={i.status} reason={i.failureReason} /></span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <div className="gv-footerbar">
        <div className="gv-footerbar-inner">
          <p className="gv-footnote">
            Pay GST on time, protect your working capital by claiming ITC hassle-free. Avoid
            disputes by sharing direct, verifiable proof to GSTN as you pay.
          </p>
          <div className="gv-footbtns">
            <button className="gv-secbtn" onClick={handleReportClick}>
              {gstnReportedGstins.has(effectiveSelected) && <CheckCircle2 size={15} />} Report Payments to GSTN
            </button>
            <button
              className="gv-genbtn"
              disabled={!selectedRow}
              onClick={() => {
                if (!selectedRow || (selectedRow.pendingCount === 0 && !selectedRow.hasInConsolidation)) {
                  flashToast("No invoices to pay for this GSTIN");
                  return;
                }
                onConsolidate({ from, to, buyerGstin: effectiveSelected });
              }}
            >
              Proceed to Pay
              <ArrowRight size={15} />
            </button>
          </div>
        </div>
      </div>

      {warningOpen && (
        <Modal
          title="Report Payments to GSTN"
          onClose={() => setWarningOpen(false)}
          footer={
            <>
              <button className="gv-modal-cancelbtn" onClick={() => setWarningOpen(false)}>Cancel</button>
              <button className="gv-genbtn" onClick={confirmReport}>Confirm &amp; Report</button>
            </>
          }
        >
          <div className="gv-warningbox">
            <AlertTriangle size={18} />
            <p>
              This report can be submitted only once a month, on or before the 14th, covering
              payments done for invoices created in July. Once confirmed, it cannot be
              re-submitted for this period.
            </p>
          </div>
        </Modal>
      )}

      {reportOpen && (
        <Modal
          title="GSTN Payment Report"
          subtitle={`GST Payments done to Sellers, across all GSTINs, till ${REPORT_DATE_LABEL} - for invoices up to end of July`}
          onClose={() => setReportOpen(false)}
          xwide
          footer={<button className="gv-genbtn" onClick={() => setReportOpen(false)}>Close</button>}
        >
          <UtrGroupTable groups={reportGroups} />
        </Modal>
      )}

      {toast && (
        <div className="gv-toast">
          <CheckCircle2 size={16} />
          {toast}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SCREEN 2 — Consolidated GST Payment To Sellers
// ---------------------------------------------------------------------------

function ConsolidatedScreen({ invoices, setInvoices, period, onBack, onLogoClick, onLogout }) {
  const [expanded, setExpanded] = useState(() => new Set());
  const [search, setSearch] = useState("");
  const [toast, setToast] = useState(null);
  const [payoutModalOpen, setPayoutModalOpen] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [reverseFeedData, setReverseFeedData] = useState([]); // snapshot with per-unit willSucceed/reason
  const [notifyModalOpen, setNotifyModalOpen] = useState(false);

  function flashToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(null), 3400);
  }

  // Snapshot eligible pending invoices for this buyer GSTIN + period into the consolidation.
  useEffect(() => {
    setInvoices((prev) =>
      prev.map((i) =>
        i.status === "pending" && i.buyerGstin === period.buyerGstin && i.date >= period.from && i.date <= period.to
          ? { ...i, inConsolidation: true }
          : i
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period.from, period.to, period.buyerGstin]);

  const runInvoices = useMemo(
    () =>
      invoices.filter(
        (i) => i.inConsolidation && i.buyerGstin === period.buyerGstin && i.date >= period.from && i.date <= period.to
      ),
    [invoices, period]
  );

  function updateMany(ids, patch) {
    const set = new Set(ids);
    setInvoices((prev) => prev.map((i) => (set.has(i.id) ? { ...i, ...patch } : i)));
  }

  function toggleInvoice(id, val) {
    updateMany([id], { selected: val });
  }
  function toggleUnitPending(sellerId, gstin, val) {
    const ids = runInvoices
      .filter((i) => i.sellerId === sellerId && i.gstin === gstin && (i.status === "pending" || i.status === "failed"))
      .map((i) => i.id);
    updateMany(ids, { selected: val });
  }
  function toggleAllPending(val) {
    const ids = runInvoices.filter((i) => i.status === "pending" || i.status === "failed").map((i) => i.id);
    updateMany(ids, { selected: val });
  }

  const grouped = useMemo(() => {
    return GSTIN_UNITS.map((u) => {
      const inv = runInvoices.filter((i) => i.sellerId === u.sellerId && i.gstin === u.gstin);
      const eligiblePending = inv.filter((i) => i.status === "pending" || i.status === "failed");
      const pendingCount = eligiblePending.length;
      const pendingSelectedCount = eligiblePending.filter((i) => i.selected).length;
      const inRun = inv.filter((i) => (i.status === "pending" || i.status === "failed" ? isSendable(i) : true));
      const counts = { pending: 0, inflight: 0, paid: 0, failed: 0 };
      inRun.forEach((i) => (counts[i.status] += 1));
      const gstInRun = inRun.reduce((sum, i) => sum + i.gst, 0);
      const allPaid = inRun.length > 0 && inRun.every((i) => i.status === "paid");
      return { ...u, invoices: inv, inRun, pendingCount, pendingSelectedCount, counts, gstInRun, allPaid };
    }).filter((u) => u.invoices.length > 0);
  }, [runInvoices]);

  const filtered = grouped.filter((u) => u.sellerName.toLowerCase().includes(search.toLowerCase()));

  const grand = grouped.reduce(
    (acc, u) => {
      acc.selectedPending += u.pendingSelectedCount;
      acc.inflight += u.counts.inflight;
      acc.failed += u.counts.failed;
      acc.paid += u.counts.paid;
      acc.gstInRun += u.gstInRun;
      return acc;
    },
    { selectedPending: 0, inflight: 0, failed: 0, paid: 0, gstInRun: 0 }
  );

  const totalPending = grouped.reduce((sum, u) => sum + u.pendingCount, 0);
  const allPendingSelected = grand.selectedPending === totalPending && totalPending > 0;
  const nonePendingSelected = grand.selectedPending === 0;

  // --- Payout file modal (one instruction per seller GSTIN) ---------------

  const payoutPreview = useMemo(() => {
    if (!payoutModalOpen) return [];
    return grouped
      .filter((u) => u.pendingSelectedCount > 0)
      .map((u) => {
        const invs = u.invoices.filter((i) => isSendable(i));
        return {
          key: u.key,
          name: u.sellerName,
          gstin: u.gstin,
          bankAccount: u.bankAccount,
          ifsc: u.ifsc,
          amount: invs.reduce((a, i) => a + i.gst, 0),
        };
      });
  }, [payoutModalOpen, grouped]);

  function openPayoutModal() {
    if (nonePendingSelected) return;
    setPayoutModalOpen(true);
  }
  function confirmSendPayout() {
    const ids = runInvoices.filter((i) => isSendable(i)).map((i) => i.id);
    updateMany(ids, { status: "inflight" });
    setPayoutModalOpen(false);
    flashToast(`Payout file sent - ${payoutPreview.length} instruction${payoutPreview.length > 1 ? "s" : ""} awaiting bank confirmation`);
  }

  // --- Payment Status modal (one outcome per seller-GSTIN instruction) ----
  // Always returns success with a UTR for every instruction.

  function openStatusModal() {
    const inflight = runInvoices.filter((i) => i.status === "inflight");
    if (inflight.length === 0) return;
    const snapshot = inflight.map((i) => ({ ...i, willSucceed: true, failureReason: null }));
    setReverseFeedData(snapshot);
    setStatusModalOpen(true);
  }

  const statusBySeller = useMemo(() => {
    if (!statusModalOpen) return [];
    const byUnit = {};
    reverseFeedData.forEach((i) => {
      const key = i.sellerId + "|" + i.gstin;
      if (!byUnit[key]) byUnit[key] = { key, sellerId: i.sellerId, gstin: i.gstin, items: [], willSucceed: i.willSucceed, reason: i.failureReason };
      byUnit[key].items.push(i);
    });
    return Object.values(byUnit).map((g) => {
      const seller = SELLERS.find((s) => s.id === g.sellerId);
      return {
        key: g.key,
        sellerId: g.sellerId,
        sellerName: seller.name,
        gstin: g.gstin,
        willSucceed: g.willSucceed,
        reason: g.reason,
        utr: g.willSucceed ? mockUtr(g.key + period.from) : null,
        amount: g.items.reduce((a, i) => a + i.gst, 0),
      };
    });
  }, [statusModalOpen, reverseFeedData, period]);

  const totalInstructions = statusBySeller.length;
  const totalSuccess = statusBySeller.filter((r) => r.willSucceed).length;
  const totalFailed = totalInstructions - totalSuccess;

  // --- Notify Sellers modal (grouped by UTR, with IRNs covered) -----------

  const notifyPreview = useMemo(() => {
    if (!notifyModalOpen) return [];
    const byUnit = {};
    reverseFeedData
      .filter((i) => i.willSucceed)
      .forEach((i) => {
        const key = i.sellerId + "|" + i.gstin;
        if (!byUnit[key]) {
          const seller = SELLERS.find((s) => s.id === i.sellerId);
          byUnit[key] = {
            key,
            utr: mockUtr(key + period.from),
            paymentDate: TODAY_ISO,
            fromBank: BUYER_BANK.bank, fromAccount: BUYER_BANK.account, fromIfsc: BUYER_BANK.ifsc,
            toBank: seller.bankName, toAccount: seller.bankAccount, toIfsc: seller.ifsc,
            buyerName: BUYER.name, buyerGstin: i.buyerGstin,
            sellerName: seller.name, sellerGstin: i.gstin,
            items: [],
            amount: 0,
          };
        }
        byUnit[key].items.push({ irn: i.irn, date: i.date, netAmount: i.value + i.gst, amount: i.gst });
        byUnit[key].amount += i.gst;
      });
    return Object.values(byUnit);
  }, [notifyModalOpen, reverseFeedData, period]);

  function handleNotify() {
    const succeedIds = new Set(reverseFeedData.filter((i) => i.willSucceed).map((i) => i.id));
    const failMap = {};
    reverseFeedData.filter((i) => !i.willSucceed).forEach((i) => { failMap[i.id] = i.failureReason; });
    setInvoices((prev) =>
      prev.map((inv) => {
        if (succeedIds.has(inv.id)) return { ...inv, status: "paid", failureReason: null };
        if (failMap[inv.id]) return { ...inv, status: "failed", failureReason: failMap[inv.id], selected: true };
        return inv;
      })
    );
    setStatusModalOpen(false);
    setNotifyModalOpen(false);
    setReverseFeedData([]);
    onBack();
  }

  return (
    <div className="gv-root">
      <style>{`.gv-root{${TOKENS}}` + SHARED_CSS + CONSOLIDATE_CSS}</style>

      <TopBar onLogoClick={onLogoClick} onLogout={onLogout}>
        <button className="gv-backbtn-top" onClick={onBack}>
          <ArrowLeft size={14} /> Back to Invoices
        </button>
        <div className="gv-title-row">
          <div>
            <h1 className="gv-h1">GST Payment To Sellers of {period.buyerGstin}</h1>
          </div>
          <div className="gv-titleright">
            <div className="gv-period">
              <span className="gv-dot" />
              Period: <b>{fmtDate(period.from)} - {fmtDate(period.to)}</b> - Pay GST by <b>14 Aug 2026</b>
            </div>
          </div>
        </div>
      </TopBar>

      <div className="gv-main">
        <div className="gv-summary">
          <div className="gv-card">
            <p className="gv-card-label">Selected / Pending GST Payments</p>
            <div className="gv-card-value">{grand.selectedPending} <span className="gv-card-value-sub">/ {totalPending}</span></div>
            <p className="gv-card-sub">Ready to include in payout file</p>
          </div>
          <div className="gv-card">
            <p className="gv-card-label">Payment Initiated / Failed</p>
            <div className="gv-card-value accent-teal">{grand.inflight} <span className="gv-card-value-sub">/ {grand.failed} failed</span></div>
            <p className="gv-card-sub">Re-initiate Failed Payment after resolving the failure cause</p>
          </div>
          <div className="gv-card">
            <p className="gv-card-label">Total GST Payment (In Run)</p>
            <div className="gv-card-value accent">{money(grand.gstInRun)}</div>
            <p className="gv-card-sub">{grand.paid} of {grand.selectedPending + grand.inflight + grand.failed + grand.paid} invoices paid</p>
          </div>
        </div>

        <div className="gv-toolbar">
          <div className="gv-search">
            <Search size={15} />
            <input type="text" placeholder="Search seller name" value={search} onChange={(e) => setSearch(e.target.value)} />
            {search.length > 0 && (
              <button className="gv-searchclear" onClick={() => setSearch("")} aria-label="Clear search">
                <X size={13} />
              </button>
            )}
          </div>
          <div className="gv-toolbtns">
            <button className="gv-linkbtn" onClick={() => toggleAllPending(true)}>Select all pending</button>
            <span className="gv-sep">|</span>
            <button className="gv-linkbtn" onClick={() => toggleAllPending(false)}>Deselect all</button>
            <span className="gv-sep">|</span>
            <button
              className="gv-linkbtn"
              onClick={() => setExpanded(expanded.size === filtered.length ? new Set() : new Set(filtered.map((u) => u.key)))}
            >
              {expanded.size === filtered.length && filtered.length > 0 ? "Collapse all" : "Expand all"}
            </button>
          </div>
        </div>

        <div className="gv-table-wrap">
          <div className="gv-thead">
            <div><Check checked={allPendingSelected} indeterminate={!allPendingSelected && !nonePendingSelected} onChange={() => toggleAllPending(!allPendingSelected)} label="Select all pending invoices" /></div>
            <div></div>
            <div>Seller</div>
            <div>Status</div>
            <div>GST Amount</div>
          </div>

          {filtered.length === 0 && <div className="gv-empty">No sellers match "{search}".</div>}

          {filtered.map((u) => {
            const isOpen = expanded.has(u.key);
            const sellerAllSel = u.pendingSelectedCount === u.pendingCount && u.pendingCount > 0;
            const sellerNoneSel = u.pendingSelectedCount === 0;
            return (
              <div className="gv-group" key={u.key}>
                <div
                  className="gv-group-row"
                  onClick={() => {
                    const next = new Set(expanded);
                    next.has(u.key) ? next.delete(u.key) : next.add(u.key);
                    setExpanded(next);
                  }}
                >
                  <div>
                    {u.pendingCount > 0 ? (
                      <Check checked={sellerAllSel} indeterminate={!sellerAllSel && !sellerNoneSel} onChange={() => toggleUnitPending(u.sellerId, u.gstin, !sellerAllSel)} label={`Select all pending invoices for ${u.sellerName}`} />
                    ) : null}
                  </div>
                  <div className="gv-chevron">{isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</div>
                  <div>
                    <div className="gv-seller-name">
                      <Building2 size={13} style={{ marginRight: 6, verticalAlign: -2, color: "var(--muted2)" }} />
                      {u.sellerName}
                    </div>
                    <div className="gv-seller-gstin">{u.gstin}</div>
                  </div>
                  <div className="gv-chips">
                    {u.counts.pending > 0 && <span className="gv-chip pending">{u.counts.pending} Pending</span>}
                    {u.counts.inflight > 0 && <span className="gv-chip inflight">{u.counts.inflight} Payment Initiated</span>}
                    {u.counts.failed > 0 && <span className="gv-chip failed">{u.counts.failed} Failed</span>}
                    {u.counts.paid > 0 && <span className="gv-chip paid">{u.counts.paid} Paid</span>}
                    {u.inRun.length === 0 && <span className="gv-chip muted">Nothing selected</span>}
                  </div>
                  <div className={"gv-amount" + (u.inRun.length === 0 ? " dim" : "")}>{money(u.gstInRun)}</div>
                </div>

                {isOpen && (
                  <div className="gv-invoices">
                    {u.invoices.filter((i) => i.status !== "paid").map((i) => {
                      const selectable = i.status === "pending" || i.status === "failed";
                      const dimmed = selectable && !i.selected;
                      return (
                        <div className={"gv-invcard" + (dimmed ? " off" : "")} key={i.id}>
                          <div className="gv-irncard-row gv-irncard-row1">
                            <span className="l">IRN</span>
                            <span className="v gv-irn" title={i.irn}>{i.irn}</span>
                          </div>
                          <div className="gv-invcard-row2">
                            <div className="gv-irnfield gv-irnfield-check">
                              {selectable ? (
                                <Check checked={i.selected} onChange={() => toggleInvoice(i.id, !i.selected)} label={`Include ${i.irn}`} />
                              ) : null}
                            </div>
                            <div className="gv-irnfield">
                              <span className="l">Date</span>
                              <span className="v">{fmtDate(i.date)}</span>
                            </div>
                            <div className="gv-irnfield">
                              <span className="l">Invoice Value</span>
                              <span className="v">{money(i.value + i.gst)}</span>
                            </div>
                            <div className="gv-irnfield">
                              <span className="l">GST Amount</span>
                              <span className="v">{money(i.gst)}</span>
                            </div>
                            <div className="gv-irnfield">
                              <span className="l">Status</span>
                              <span className="v"><StatusBadge status={i.status} reason={i.failureReason} /></span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="gv-footerbar">
        <div className="gv-footerbar-inner">
          <p className="gv-footnote">
            Pay GST on time, protect your working capital by claiming ITC hassle-free. Avoid
            disputes by sharing direct, verifiable proof to GSTN of all your GST component
            payments to your Sellers.
          </p>
          <div className="gv-footbtns">
            <button className="gv-secbtn" disabled={grand.inflight === 0} onClick={openStatusModal}>
              <Inbox size={15} /> Update UTR
            </button>
            <button className="gv-genbtn" disabled={nonePendingSelected} onClick={openPayoutModal}>
              <Send size={15} /> Create Payout File
            </button>
          </div>
        </div>
      </div>

      {payoutModalOpen && (
        <Modal
          title="Payout File Preview"
          subtitle={`One payment instruction per seller - My GSTIN: ${period.buyerGstin} - ${fmtDate(period.from)} - ${fmtDate(period.to)}`}
          onClose={() => setPayoutModalOpen(false)}
          wide
          footer={
            <>
              <button className="gv-modal-cancelbtn" onClick={() => setPayoutModalOpen(false)}>Cancel</button>
              <button className="gv-genbtn" onClick={confirmSendPayout}><Send size={15} /> Send</button>
            </>
          }
        >
          <table className="gv-mtable">
            <thead>
              <tr>
                <th>Seller</th>
                <th>Bank A/C</th>
                <th>IFSC</th>
                <th className="num">GST Amount</th>
              </tr>
            </thead>
            <tbody>
              {payoutPreview.map((p) => (
                <tr key={p.key}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{p.name}</div>
                    <div style={{ fontSize: "0.74rem", color: "var(--muted2)" }}>{p.gstin}</div>
                  </td>
                  <td>{p.bankAccount}</td>
                  <td>{p.ifsc}</td>
                  <td className="num">{money(p.amount)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={3}>Total - {payoutPreview.length} instruction{payoutPreview.length !== 1 ? "s" : ""}</td>
                <td className="num">{money(payoutPreview.reduce((a, p) => a + p.amount, 0))}</td>
              </tr>
            </tfoot>
          </table>
        </Modal>
      )}

      {statusModalOpen && !notifyModalOpen && (
        <Modal
          title="Update UTR"
          subtitle={`One outcome per seller payment instruction - My GSTIN: ${period.buyerGstin}`}
          onClose={() => setStatusModalOpen(false)}
          wide
          footer={
            <button className="gv-outlinebtn" onClick={() => setNotifyModalOpen(true)}>Notify Sellers</button>
          }
        >
          <table className="gv-mtable">
            <thead>
              <tr>
                <th>Seller</th>
                <th>UTR Number</th>
                <th className="num">Amount</th>
              </tr>
            </thead>
            <tbody>
              {statusBySeller.map((r) => (
                <tr key={r.key}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{r.sellerName}</div>
                    <div style={{ fontSize: "0.74rem", color: "var(--muted2)" }}>{r.gstin}</div>
                    {!r.willSucceed && <div className="gv-modal-failnote">{r.reason}</div>}
                  </td>
                  <td>{r.utr ? <span className="gv-utr">{r.utr}</span> : <span className="gv-forbidden" title="Payment failed">{FORBIDDEN}</span>}</td>
                  <td className="num">{money(r.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="gv-modal-summary">
            <span><b>{totalInstructions}</b> Total Instructions</span>
            <span className="ok"><b>{totalSuccess}</b> Successful</span>
            <span className="bad"><b>{totalFailed}</b> Failed</span>
          </div>
        </Modal>
      )}

      {notifyModalOpen && (
        <Modal
          title="Notify Sellers"
          subtitle={`IRN-level payment confirmation - My GSTIN: ${period.buyerGstin}`}
          onClose={() => setNotifyModalOpen(false)}
          xwide
          footer={
            <button className="gv-genbtn" onClick={handleNotify}>Notify</button>
          }
        >
          <UtrGroupTable groups={notifyPreview} detailed />
        </Modal>
      )}

      {toast && (
        <div className="gv-toast">
          <CheckCircle2 size={16} />
          {toast}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared + screen-specific CSS
// ---------------------------------------------------------------------------

const LOGIN_CSS = `
  .gv-login-root { min-height: 100vh; display: flex; align-items: center; justify-content: center; background: linear-gradient(135deg, var(--maroon-dark) 0%, var(--navy) 55%, var(--maroon) 100%); font-family: Inter, -apple-system, "Segoe UI", Roboto, Arial, sans-serif; padding: 1.5rem; box-sizing: border-box; }
  .gv-login-root * { box-sizing: border-box; }
  .gv-login-card { background: #fff; border-radius: 16px; padding: 2.2rem 2rem; width: 100%; max-width: 24rem; box-shadow: 0 24px 60px rgba(0,0,0,0.35); }
  .gv-login-logo { display: flex; justify-content: center; margin-bottom: 1.4rem; }
  .gv-login-form h1 { font-size: 1.35rem; font-weight: 700; color: var(--navy); margin: 0 0 0.3rem 0; text-align: center; }
  .gv-login-sub { font-size: 0.85rem; color: var(--muted); text-align: center; margin: 0 0 1.2rem 0; line-height: 1.4; }
  .gv-login-form label { display: block; font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--muted); margin: 0.9rem 0 0.35rem; }
  .gv-login-form input[type="text"], .gv-login-form input[type="password"] { width: 100%; padding: 0.7rem 0.9rem; border: 1px solid var(--border); border-radius: 8px; font-size: 0.9rem; outline: none; color: var(--ink); }
  .gv-login-form input:focus { border-color: var(--blue); box-shadow: 0 0 0 3px rgba(29,78,216,0.12); }
  .gv-login-pwdwrap { position: relative; }
  .gv-login-pwdwrap input { padding-right: 2.6rem; }
  .gv-login-pwdwrap button { position: absolute; right: 0.6rem; top: 50%; transform: translateY(-50%); background: none; border: none; color: var(--muted2); cursor: pointer; display: flex; padding: 0.2rem; }
  .gv-login-otp { width: 100%; padding: 0.9rem; border: 1px solid var(--border); border-radius: 8px; font-size: 1.4rem; letter-spacing: 0.6rem; text-align: center; outline: none; font-variant-numeric: tabular-nums; color: var(--ink); }
  .gv-login-otp:focus { border-color: var(--blue); box-shadow: 0 0 0 3px rgba(29,78,216,0.12); }
  .gv-login-error { color: var(--red-ink); background: var(--red-bg); border-radius: 8px; padding: 0.5rem 0.7rem; font-size: 0.8rem; margin-top: 0.9rem; }
  .gv-login-submit { width: 100%; justify-content: center; margin-top: 1.3rem; }
  .gv-login-back { width: 100%; background: none; border: none; color: var(--blue); font-size: 0.82rem; font-weight: 600; margin-top: 0.7rem; cursor: pointer; padding: 0.4rem; }
  .gv-login-hint { text-align: center; font-size: 0.72rem; color: var(--muted2); margin-top: 1rem; }
`;

const SHARED_CSS = `
  .gv-root { font-family: Inter, -apple-system, "Segoe UI", Roboto, Arial, sans-serif; background: var(--bg); color: var(--ink); min-height: 100vh; padding: 0 0 6.5rem 0; box-sizing: border-box; }
  .gv-root * { box-sizing: border-box; }
  .gv-topbar { background: linear-gradient(100deg, var(--maroon-dark) 0%, var(--navy) 55%, var(--maroon) 100%); color: #fff; padding: 0.9rem 2rem 1.1rem; }
  .gv-topbar-row1 { display: flex; align-items: center; justify-content: space-between; gap: 0.7rem; padding-bottom: 0.6rem; margin-bottom: 0.6rem; border-bottom: 1px solid rgba(255,255,255,0.18); flex-wrap: wrap; }
  .gv-topbar-left { display: flex; align-items: center; gap: 0.7rem; flex-wrap: wrap; }
  .gv-topbar-chips { display: flex; flex-direction: row; align-items: center; gap: 0.6rem; }
  .gv-topbar-right { display: flex; align-items: center; gap: 0.9rem; flex-wrap: wrap; }
  .gv-usermenu { position: relative; }
  .gv-usericon { width: 2.3rem; height: 2.3rem; border-radius: 50%; background: rgba(255,255,255,0.15); border: 1px solid rgba(255,255,255,0.28); color: #fff; display: flex; align-items: center; justify-content: center; cursor: pointer; flex-shrink: 0; }
  .gv-usericon:hover { background: rgba(255,255,255,0.26); }
  .gv-usermenu-dropdown { position: absolute; top: calc(100% + 0.5rem); right: 0; background: #fff; border: 1px solid var(--border); border-radius: 10px; box-shadow: 0 12px 28px rgba(0,0,0,0.18); min-width: 9.5rem; overflow: hidden; z-index: 40; }
  .gv-usermenu-item { display: block; width: 100%; text-align: left; padding: 0.65rem 1rem; background: none; border: none; font-size: 0.85rem; color: var(--ink); cursor: pointer; }
  .gv-usermenu-item:hover { background: #F7FAFC; }
  .gv-usermenu-item + .gv-usermenu-item { border-top: 1px solid var(--border); }
  .gv-todaydate { font-size: 0.72rem; color: #E0C3CC; font-weight: 600; }
  .gv-logo { background: #fff; border: none; border-radius: 9px; height: 2.6rem; padding: 0.3rem 0.75rem; display: flex; align-items: center; justify-content: center; cursor: pointer; flex-shrink: 0; box-shadow: 0 1px 3px rgba(0,0,0,0.25); transition: transform 0.12s ease; }
  .gv-logo:hover { transform: translateY(-1px); box-shadow: 0 3px 8px rgba(0,0,0,0.3); }
  .gv-logofull { display: flex; align-items: center; gap: 0.45rem; }
  .gv-logotext { font-weight: 800; font-size: 1.08rem; color: var(--navy); letter-spacing: 0.01em; line-height: 1; }
  .gv-logotext sup { font-size: 0.5rem; font-weight: 700; margin-left: 1px; }
  .gv-backbtn-top { background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.2); color: #F3E7EC; font-size: 0.78rem; font-weight: 600; border-radius: 7px; padding: 0.45rem 0.8rem; display: inline-flex; align-items: center; gap: 0.4rem; cursor: pointer; margin-bottom: 0.6rem; }
  .gv-backbtn-top:hover { background: rgba(255,255,255,0.18); }
  .gv-eyebrow { font-size: 0.72rem; font-weight: 700; letter-spacing: 0.14em; color: #F2C9D6; text-transform: uppercase; margin: 0; }
  .gv-rolechip { display: inline-flex; align-items: center; gap: 0.35rem; background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.18); color: #F3E7EC; font-size: 0.72rem; font-weight: 600; padding: 0.2rem 0.6rem; border-radius: 999px; }
  .gv-rolechip svg { color: #F2C9D6; }
  .gv-title-row { display: flex; align-items: center; justify-content: space-between; gap: 1rem; flex-wrap: wrap; }
  .gv-h1 { font-size: 1.5rem; font-weight: 700; margin: 0; letter-spacing: -0.01em; }
  .gv-sub { font-size: 0.86rem; color: #EBD9DF; margin: 0.4rem 0 0 0; max-width: 46rem; line-height: 1.5; }
  .gv-period { display: flex; align-items: center; gap: 0.5rem; background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.18); border-radius: 999px; padding: 0.45rem 0.9rem; font-size: 0.78rem; color: #F3E7EC; white-space: nowrap; }
  .gv-period b { color: #fff; }
  .gv-titleright { display: flex; align-items: center; gap: 0.6rem; flex-wrap: wrap; }
  .gv-mygstin-chip { padding: 0.45rem 0.9rem; font-size: 0.78rem; box-sizing: border-box; }
  .gv-dot { width: 6px; height: 6px; border-radius: 50%; background: #F2C9D6; display: inline-block; }
  .gv-main { max-width: 84rem; margin: 0 auto; padding: 1rem 2rem 0; }
  .gv-summary { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1rem; margin-bottom: 1.4rem; }
  .gv-summary-flex { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 0.7rem; margin-bottom: 1rem; }
  .gv-card-label-note { text-transform: none; font-weight: 500; letter-spacing: 0; color: var(--muted2); font-size: 0.68rem; }
  .gv-card { background: var(--card); border: 1px solid var(--border); border-radius: 12px; padding: 0.8rem 1.1rem; }
  .gv-card-alert { border-color: #F3D9A6; background: linear-gradient(180deg, #FFFCF6, #ffffff); }
  .gv-card-progress { border-color: #BFE0EA; background: linear-gradient(180deg, #F5FBFD, #ffffff); }
  .gv-card-danger { border-color: #F0D2B8; background: linear-gradient(180deg, #FFF9F3, #ffffff); }
  .gv-card-success { border-color: #BFE0BE; background: linear-gradient(180deg, #F6FBF5, #ffffff); }
  .gv-card-label { font-size: 0.7rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; color: var(--muted); margin: 0 0 0.4rem 0; }
  .gv-card-value { font-size: 1.45rem; font-weight: 700; color: var(--navy); font-variant-numeric: tabular-nums; letter-spacing: -0.01em; }
  .gv-card-value-sub { font-size: 1rem; color: var(--muted2); font-weight: 600; }
  .gv-card-value.accent { color: var(--blue); }
  .gv-card-value.accent-amber { color: var(--amber-ink); }
  .gv-card-value.accent-teal { color: var(--teal-ink); }
  .gv-card-value.accent-green { color: var(--green-ink); }
  .gv-card-value.accent-red { color: var(--red-ink); }
  .gv-card-sub { font-size: 0.76rem; color: var(--muted2); margin-top: 0.25rem; }
  .gv-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin-bottom: 0.6rem; flex-wrap: wrap; }
  .gv-search { position: relative; width: 17rem; }
  .gv-search input { width: 100%; padding: 0.55rem 2.1rem 0.55rem 2.1rem; border: 1px solid var(--border); border-radius: 8px; font-size: 0.85rem; background: var(--card); color: var(--ink); outline: none; -webkit-appearance: none; appearance: none; }
  .gv-search input::-webkit-search-cancel-button, .gv-search input::-webkit-search-decoration { -webkit-appearance: none; display: none; }
  .gv-search input::-ms-clear { display: none; }
  .gv-search input:focus { border-color: var(--blue); box-shadow: 0 0 0 3px rgba(29,78,216,0.12); }
  .gv-search svg { position: absolute; left: 0.65rem; top: 50%; transform: translateY(-50%); color: var(--muted2); }
  .gv-searchclear { position: absolute; right: 0.5rem; top: 50%; transform: translateY(-50%); background: none; border: none; color: var(--muted2); cursor: pointer; display: flex; padding: 0.2rem; border-radius: 5px; }
  .gv-searchclear:hover { background: #F0F4F7; color: var(--ink); }
  .gv-toolbtns { display: flex; gap: 0.6rem; align-items: center; }
  .gv-linkbtn { background: none; border: none; color: var(--blue); font-size: 0.82rem; font-weight: 600; cursor: pointer; padding: 0.3rem 0.2rem; }
  .gv-linkbtn:hover { text-decoration: underline; }
  .gv-sep { color: var(--border); }
  .gv-table-wrap { background: var(--card); border: 1px solid var(--border); border-radius: 12px; overflow: hidden; }
  .gv-empty { padding: 3rem 1rem; text-align: center; color: var(--muted2); font-size: 0.9rem; }
  .gv-chk { width: 16px; height: 16px; accent-color: var(--blue); cursor: pointer; }
  .gv-irn { font-weight: 600; color: var(--ink); font-size: 0.74rem; font-variant-numeric: tabular-nums; letter-spacing: 0.01em; word-break: break-all; }
  .gv-badge { display: inline-block; font-size: 0.72rem; font-weight: 700; padding: 0.25rem 0.6rem; border-radius: 999px; white-space: nowrap; }
  .gv-badge.pending { background: var(--amber-bg); color: var(--amber-ink); }
  .gv-badge.inflight { background: var(--teal-bg); color: var(--teal-ink); }
  .gv-badge.paid { background: var(--green-bg); color: var(--green-ink); }
  .gv-badge.failed { background: var(--red-bg); color: var(--red-ink); }
  .gv-badge.tip { cursor: help; border-bottom: 1px dashed var(--red-ink); border-radius: 999px 999px 2px 2px; }
  .gv-forbidden { font-size: 0.85rem; cursor: help; opacity: 0.75; }
  .gv-invoices { background: #FBFCFD; padding: 0.6rem 1rem 0.9rem 1rem; display: flex; flex-direction: column; gap: 0.5rem; }
  .gv-footerbar { position: fixed; left: 0; right: 0; bottom: 0; background: linear-gradient(100deg, var(--navy) 0%, var(--maroon-dark) 100%); color: #fff; padding: 1rem 2rem; display: flex; align-items: center; justify-content: center; box-shadow: 0 -8px 24px rgba(11,31,51,0.25); z-index: 20; }
  .gv-footerbar-inner { max-width: 84rem; width: 100%; display: flex; align-items: center; justify-content: space-between; gap: 1.5rem; flex-wrap: wrap; }
  .gv-footbtns { display: flex; gap: 0.6rem; }
  .gv-footnote { font-size: 0.85rem; color: #EBD9DF; line-height: 1.4; max-width: 34rem; margin: 0; }
  .gv-genbtn { background: var(--blue); color: #fff; border: none; border-radius: 8px; padding: 0.7rem 1.3rem; font-size: 0.88rem; font-weight: 700; display: flex; align-items: center; gap: 0.5rem; cursor: pointer; transition: filter 0.12s ease, transform 0.12s ease; white-space: nowrap; }
  .gv-genbtn:hover:not(:disabled) { filter: brightness(1.08); transform: translateY(-1px); }
  .gv-genbtn:disabled { background: #6B7280; color: #D1D5DB; cursor: not-allowed; }
  .gv-secbtn { background: rgba(255,255,255,0.12); color: #fff; border: 1px solid rgba(255,255,255,0.3); border-radius: 8px; padding: 0.7rem 1.3rem; font-size: 0.88rem; font-weight: 700; display: flex; align-items: center; gap: 0.5rem; cursor: pointer; transition: filter 0.12s ease, transform 0.12s ease; white-space: nowrap; }
  .gv-secbtn:hover:not(:disabled) { background: rgba(255,255,255,0.2); }
  .gv-secbtn:disabled { background: rgba(255,255,255,0.05); color: rgba(255,255,255,0.35); border-color: rgba(255,255,255,0.1); cursor: not-allowed; }
  .gv-toast { position: fixed; bottom: 6.4rem; left: 50%; transform: translateX(-50%); background: var(--green-bg); border: 1px solid #8FBF83; color: var(--green-ink); padding: 0.75rem 1.1rem; border-radius: 10px; display: flex; align-items: center; gap: 0.55rem; font-size: 0.85rem; font-weight: 600; box-shadow: 0 10px 24px rgba(0,0,0,0.12); z-index: 30; animation: gv-rise 0.25s ease; max-width: 32rem; text-align: left; }
  @keyframes gv-rise { from { opacity: 0; transform: translate(-50%, 8px); } to { opacity: 1; transform: translate(-50%, 0); } }
  .gv-modal-overlay { position: fixed; inset: 0; background: rgba(36,18,47,0.55); display: flex; align-items: center; justify-content: center; z-index: 50; padding: 1.5rem; }
  .gv-modal { background: #fff; border-radius: 14px; width: 100%; max-width: 40rem; max-height: 85vh; display: flex; flex-direction: column; box-shadow: 0 24px 60px rgba(0,0,0,0.28); }
  .gv-modal.wide { max-width: 48rem; }
  .gv-modal.xwide { max-width: 74rem; }
  .gv-modal-head { display: flex; align-items: flex-start; justify-content: space-between; padding: 1.3rem 1.5rem 1rem; border-bottom: 1px solid var(--border); }
  .gv-modal-head h2 { margin: 0; font-size: 1.15rem; font-weight: 700; color: var(--navy); }
  .gv-modal-head p { margin: 0.3rem 0 0; font-size: 0.8rem; color: var(--muted); }
  .gv-modal-close { background: none; border: none; cursor: pointer; color: var(--muted2); padding: 0.3rem; border-radius: 6px; display: flex; }
  .gv-modal-close:hover { background: #F0F4F7; }
  .gv-modal-body { padding: 1.2rem 1.5rem; overflow-y: auto; flex: 1; }
  .gv-modal-foot { padding: 1rem 1.5rem; border-top: 1px solid var(--border); display: flex; justify-content: flex-end; gap: 0.6rem; align-items: center; }
  .gv-modal-cancelbtn { background: #fff; border: 1px solid var(--border); color: var(--muted); font-weight: 600; font-size: 0.85rem; padding: 0.6rem 1.1rem; border-radius: 8px; cursor: pointer; }
  .gv-modal-cancelbtn:hover { background: #F7FAFC; }
  .gv-modal-failnote { font-size: 0.74rem; color: var(--red-ink); margin-top: 0.15rem; }
  .gv-modal-summary { display: flex; gap: 1.5rem; justify-content: center; background: #F7F8FA; border: 1px solid var(--border); border-radius: 10px; padding: 0.8rem 1rem; margin-top: 1rem; font-size: 0.82rem; color: var(--muted); }
  .gv-modal-summary b { font-size: 1rem; color: var(--navy); }
  .gv-modal-summary .ok b { color: var(--green-ink); }
  .gv-modal-summary .bad b { color: var(--red-ink); }
  .gv-warningbox { display: flex; align-items: flex-start; gap: 0.7rem; background: var(--amber-bg); border: 1px solid #F3D9A6; border-radius: 10px; padding: 1rem 1.1rem; }
  .gv-warningbox svg { color: var(--amber-ink); flex-shrink: 0; margin-top: 0.1rem; }
  .gv-warningbox p { margin: 0; font-size: 0.86rem; color: #6B4B00; line-height: 1.5; }
  .gv-mtable { width: 100%; border-collapse: collapse; font-size: 0.83rem; }
  .gv-mtable th { text-align: left; font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--muted); font-weight: 700; padding: 0.5rem 0.6rem; border-bottom: 1px solid var(--border); white-space: nowrap; }
  .gv-mtable td { padding: 0.6rem; border-bottom: 1px solid #EEF2F5; vertical-align: top; white-space: nowrap; }
  .gv-mtable td.num, .gv-mtable th.num { text-align: right; font-variant-numeric: tabular-nums; }
  .gv-mtable tbody tr:last-child td { border-bottom: none; }
  .gv-mtable tfoot td { font-weight: 700; border-top: 2px solid var(--border); border-bottom: none; padding-top: 0.7rem; }
  .gv-irnlist { display: flex; flex-direction: column; gap: 0.7rem; }
  .gv-irncard { border: 1px solid var(--border); border-radius: 10px; overflow: hidden; }
  .gv-invcard { border: 1px solid var(--border); border-radius: 10px; overflow: hidden; background: #fff; }
  .gv-invcard.off { opacity: 0.42; }
  .gv-invcard-row2 { display: flex; flex-wrap: wrap; gap: 1.3rem; padding: 0.6rem 0.9rem; background: #F8FAFC; }
  .gv-irnfield-check { justify-content: center; min-width: 1.7rem; }
  .gv-irncard-row { display: flex; flex-wrap: wrap; gap: 1.3rem; padding: 0.55rem 0.9rem; }
  .gv-irncard-row1 { background: #fff; align-items: baseline; gap: 0.6rem; }
  .gv-irncard-row1 .l { font-size: 0.64rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--muted2); flex-shrink: 0; }
  .gv-irncard-row1 .v { font-size: 0.78rem; word-break: break-all; }
  .gv-irncard-row2 { background: #F8FAFC; border-top: 1px dashed var(--border); }
  .gv-irncard-row3 { flex-direction: column; gap: 0.4rem; background: #fff; border-top: 1px dashed var(--border); }
  .gv-irncard-row3 .l { font-size: 0.64rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--muted2); }
  .gv-invoicelines { display: flex; flex-direction: column; gap: 0.3rem; width: 100%; }
  .gv-invoiceline { display: flex; justify-content: space-between; align-items: center; gap: 1rem; padding: 0.35rem 0.6rem; background: #F0F4F7; border-radius: 6px; }
  .gv-invoiceline .gv-irn { font-size: 0.72rem; word-break: break-all; color: var(--ink); }
  .gv-invoiceline-amt { font-weight: 600; font-variant-numeric: tabular-nums; color: var(--ink); flex-shrink: 0; }
  .gv-invoiceline-detailed, .gv-invoiceline-head { justify-content: flex-start; gap: 0; }
  .gv-invoiceline-head { background: none; padding: 0 0.6rem; font-size: 0.62rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--muted2); }
  .gv-il-irn { flex: 1 1 auto; min-width: 0; font-size: 0.72rem; word-break: break-all; padding-right: 0.6rem; }
  .gv-il-date { flex: 0 0 6.5rem; color: var(--muted); font-variant-numeric: tabular-nums; font-size: 0.78rem; }
  .gv-il-net { flex: 0 0 7rem; text-align: right; font-variant-numeric: tabular-nums; font-size: 0.78rem; padding-right: 0.8rem; }
  .gv-il-gst { flex: 0 0 7rem; text-align: right; font-variant-numeric: tabular-nums; font-size: 0.78rem; font-weight: 600; }
  .gv-invoiceline-head .gv-il-net, .gv-invoiceline-head .gv-il-gst { font-weight: 700; }
  .gv-irnfield { display: flex; flex-direction: column; gap: 0.15rem; min-width: 8rem; }
  .gv-irnfield .l { font-size: 0.64rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--muted2); }
  .gv-irnfield .v { font-size: 0.82rem; color: var(--ink); font-variant-numeric: tabular-nums; }
  .gv-irnlist-total { display: flex; justify-content: space-between; padding: 0.7rem 0.9rem; font-size: 0.85rem; font-weight: 700; color: var(--navy); border-top: 2px solid var(--border); margin-top: 0.3rem; }
  .gv-utr { font-variant-numeric: tabular-nums; font-weight: 600; color: var(--teal-ink); }
  .gv-outlinebtn { background: #fff; border: 1.5px solid var(--blue); color: var(--blue); font-weight: 700; font-size: 0.88rem; padding: 0.66rem 1.2rem; border-radius: 8px; cursor: pointer; display: flex; align-items: center; gap: 0.5rem; }
  .gv-outlinebtn:hover { background: #F0F3FA; }
  .gv-notify-status { display: flex; flex-direction: column; gap: 0.2rem; }
  .gv-notify-line { display: flex; align-items: center; gap: 0.4rem; font-size: 0.8rem; font-weight: 600; color: var(--ink); }
  .gv-notify-line svg { color: var(--green-ink); flex-shrink: 0; }
  @media (max-width: 720px) {
    .gv-summary-flex { grid-template-columns: 1fr 1fr; }
  }
`;

const LIST_CSS = `
  .gv-headright { display: flex; align-items: center; gap: 0.6rem; }
  .gv-periodbox { display: flex; align-items: center; gap: 0.5rem; background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.18); border-radius: 10px; padding: 0.5rem 0.8rem; font-size: 0.8rem; color: #F3E7EC; }
  .gv-periodbox svg { color: #F2C9D6; flex-shrink: 0; }
  .gv-dateinput { background: transparent; border: none; color: #fff; font-size: 0.8rem; font-family: inherit; outline: none; color-scheme: dark; }
  .gv-periodto { color: #E0C3CC; }
  .gv-periodpill { background: #fff; color: var(--maroon); font-weight: 700; font-size: 0.68rem; padding: 0.2rem 0.5rem; border-radius: 999px; margin-left: 0.2rem; text-transform: uppercase; letter-spacing: 0.04em; }
  .gv-table-cards { background: transparent; border: none; }
  .gv-gstin-thead { display: grid; grid-template-columns: 2.2rem 2.4rem 12rem repeat(5, 1fr); gap: 0.9rem; padding: 0.55rem 1.1rem; background: #F8FAFC; border: 1px solid var(--border); border-radius: 10px 10px 0 0; font-size: 0.68rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--muted); }
  .gv-gstin-row { display: grid; grid-template-columns: 2.2rem 2.4rem 12rem repeat(5, 1fr); gap: 0.9rem; align-items: center; padding: 0.6rem 1.1rem; border: 1px solid var(--border); border-top: none; background: var(--card); font-size: 0.83rem; cursor: pointer; transition: background 0.12s ease; }
  .gv-gstin-row:hover { background: #F7FAFC; }
  .gv-gstin-row.selected { background: #F0F4FB; box-shadow: inset 3px 0 0 var(--blue); }
  .gv-gstin-chevron { background: none; border: none; color: var(--muted2); cursor: pointer; display: flex; align-items: center; justify-content: center; padding: 0.2rem; border-radius: 5px; }
  .gv-gstin-chevron:hover { background: var(--grey-bg); color: var(--ink); }
  .gv-gstin-radio input { width: 17px; height: 17px; accent-color: var(--blue); cursor: pointer; }
  .gv-gstin-cell { display: flex; flex-direction: column; gap: 0.1rem; }
  .gv-gstin-value { font-weight: 700; color: var(--ink); font-variant-numeric: tabular-nums; font-size: 0.82rem; }
  .gv-gstin-sub { font-size: 0.72rem; color: var(--muted2); }
  .gv-gstin-metric { display: flex; flex-direction: column; gap: 0.1rem; }
  .gv-gstin-amt { font-weight: 700; font-variant-numeric: tabular-nums; color: var(--navy); font-size: 0.86rem; }
  .gv-gstin-amt.accent-amber { color: var(--amber-ink); }
  .gv-gstin-amt.accent-teal { color: var(--teal-ink); }
  .gv-gstin-amt.accent-red { color: var(--red-ink); }
  .gv-gstin-amt.accent-green { color: var(--green-ink); }
  .gv-gstin-count { font-size: 0.7rem; color: var(--muted2); }
  .gv-gstin-drilldown { border: 1px solid var(--border); border-top: none; background: #FBFCFD; padding: 0.7rem 1rem; display: flex; flex-direction: column; gap: 0.6rem; }
  .gv-gstin-drilldown:last-child { border-radius: 0 0 10px 10px; }
  .gv-invcard-row1 { display: flex; align-items: baseline; gap: 0.6rem; padding: 0.55rem 0.9rem; border-bottom: 1px dashed var(--border); }
  .gv-invcard-row1 .l { font-size: 0.64rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--muted2); flex-shrink: 0; }
  .gv-invcard-row1 .v { font-size: 0.78rem; word-break: break-all; }
  @media (max-width: 900px) {
    .gv-gstin-thead, .gv-gstin-row { grid-template-columns: 1.8rem 2rem 9rem repeat(2, 1fr); }
    .gv-gstin-thead > div:nth-child(6), .gv-gstin-thead > div:nth-child(7), .gv-gstin-thead > div:nth-child(8),
    .gv-gstin-row > .gv-gstin-metric:nth-of-type(3), .gv-gstin-row > .gv-gstin-metric:nth-of-type(4), .gv-gstin-row > .gv-gstin-metric:nth-of-type(5) { display: none; }
  }
`;

const CONSOLIDATE_CSS = `
  .gv-thead { display: grid; grid-template-columns: 2.6rem 1.7rem 1fr 13rem 9rem; padding: 0.7rem 1.1rem; background: #F8FAFC; border-bottom: 1px solid var(--border); font-size: 0.7rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--muted); gap: 0.7rem; }
  .gv-thead > div:nth-child(5) { text-align: right; }
  .gv-group { border-bottom: 1px solid var(--border); }
  .gv-group:last-child { border-bottom: none; }
  .gv-group-row { display: grid; grid-template-columns: 2.6rem 1.7rem 1fr 13rem 9rem; align-items: center; padding: 0.6rem 1.1rem; cursor: pointer; transition: background 0.12s ease; gap: 0.7rem; }
  .gv-group-row:hover { background: #F7FAFC; }
  .gv-chevron { color: var(--muted2); display: flex; }
  .gv-seller-name { font-weight: 600; font-size: 0.92rem; color: var(--ink); display: flex; align-items: center; }
  .gv-seller-gstin { font-size: 0.74rem; color: var(--muted2); margin-top: 0.1rem; font-variant-numeric: tabular-nums; }
  .gv-chips { display: flex; flex-wrap: wrap; gap: 0.3rem; }
  .gv-chip { font-size: 0.68rem; font-weight: 700; padding: 0.2rem 0.5rem; border-radius: 999px; white-space: nowrap; }
  .gv-chip.pending { background: var(--amber-bg); color: var(--amber-ink); }
  .gv-chip.inflight { background: var(--teal-bg); color: var(--teal-ink); }
  .gv-chip.paid { background: var(--green-bg); color: var(--green-ink); }
  .gv-chip.failed { background: var(--red-bg); color: var(--red-ink); }
  .gv-chip.muted { background: #EEF2F5; color: var(--muted2); }
  .gv-amount { text-align: right; font-size: 0.98rem; font-weight: 700; color: var(--blue); font-variant-numeric: tabular-nums; }
  .gv-amount.dim { color: var(--muted2); font-weight: 600; }
  @media (max-width: 900px) {
    .gv-thead, .gv-group-row { grid-template-columns: 2.2rem 1.5rem 1fr 8rem; }
    .gv-thead > div:nth-child(4), .gv-thead > div:nth-child(5),
    .gv-group-row > div:nth-child(4) { display: none; }
  }
`;

// ---------------------------------------------------------------------------
// App shell — screen router
// ---------------------------------------------------------------------------

export default function GstInvoiceFlowApp2() {
  const [loggedIn, setLoggedIn] = useState(false);
  const [invoices, setInvoices] = useState(freshInvoices);
  const [screen, setScreen] = useState("list");
  const [period, setPeriod] = useState({ from: DEFAULT_FROM, to: DEFAULT_TO, buyerGstin: null });
  const [gstnReportedGstins, setGstnReportedGstins] = useState(() => new Set());

  function goHome() {
    setInvoices(freshInvoices());
    setPeriod({ from: DEFAULT_FROM, to: DEFAULT_TO, buyerGstin: null });
    setGstnReportedGstins(new Set());
    setScreen("list");
  }

  function handleLogout() {
    setScreen("list");
    setLoggedIn(false);
  }

  if (!loggedIn) {
    return <LoginScreen onLoginSuccess={() => setLoggedIn(true)} />;
  }

  if (screen === "consolidate") {
    return (
      <ConsolidatedScreen
        invoices={invoices}
        setInvoices={setInvoices}
        period={period}
        onBack={() => setScreen("list")}
        onLogoClick={goHome}
        onLogout={handleLogout}
      />
    );
  }

  return (
    <InvoiceListScreen
      invoices={invoices}
      onLogoClick={goHome}
      onLogout={handleLogout}
      gstnReportedGstins={gstnReportedGstins}
      setGstnReportedGstins={setGstnReportedGstins}
      onConsolidate={(p) => {
        setPeriod(p);
        setScreen("consolidate");
      }}
    />
  );
}
